import React, { useEffect, useState } from 'react';
import { auth, db } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useNavigate, Link } from 'react-router-dom';
import { collection, getDocs, query, where, doc, updateDoc } from 'firebase/firestore';
import Swal from 'sweetalert2';

const Dashboard = () => {
  const [user, loadingAuth] = useAuthState(auth);
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [editData, setEditData] = useState({});
  const [myCourses, setMyCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);

  useEffect(() => {
    if (!loadingAuth && !user) {
      navigate('/login');
    }
  }, [user, loadingAuth, navigate]);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const snap = await getDocs(collection(db, 'users'));
        setStudents(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        Swal.fire('خطأ', error.message, 'error');
      } finally {
        setLoadingStudents(false);
      }
    };
    if (user) fetchStudents();
  }, [user]);

  useEffect(() => {
    const fetchMyCourses = async () => {
      try {
        const q = query(
          collection(db, 'courses'),
          where('uid', '==', user.uid)
        );
        const snap = await getDocs(q);
        setMyCourses(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        Swal.fire('خطأ', error.message, 'error');
      } finally {
        setLoadingCourses(false);
      }
    };
    if (user) fetchMyCourses();
  }, [user]);

  const handleEditChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  const handleEditSubmit = async () => {
    try {
      await updateDoc(doc(db, 'users', selectedStudent.id), editData);
      Swal.fire('تم التحديث', 'تم حفظ التعديلات بنجاح', 'success');
      setSelectedStudent(null);
    } catch (error) {
      Swal.fire('خطأ', error.message, 'error');
    }
  };

  const handleReset = async (studentId) => {
    try {
      await updateDoc(doc(db, 'users', studentId), {
        attendance: 0,
        warnings: 0,
        completedLectures: 0,
        notes: 'جاري التقييم'
      });
      Swal.fire('تم إعادة التعيين', 'تمت إعادة بيانات الطالب بنجاح', 'success');
    } catch (error) {
      Swal.fire('خطأ', error.message, 'error');
    }
  };

  if (loadingAuth || loadingStudents || loadingCourses) {
    return <div className="container mt-5">جاري التحميل...</div>;
  }

  return (
    <div className="container mt-5" style={{ direction: 'rtl' }}>
      <h2 className="mb-4">لوحة تحكم المسؤول</h2>
      <p>مرحبًا، <strong>{user.email}</strong></p>

      <h4 className="mt-4">قائمة الطلاب</h4>
      {students.length === 0 ? (
        <p>لا يوجد طلاب حتى الآن.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered table-striped">
            <thead>
              <tr>
                <th>الاسم</th>
                <th>البريد الإلكتروني</th>
                <th>الحضور</th>
                <th>الإنذارات</th>
                <th>محاضرات مكتملة</th>
                <th>ملاحظات</th>
                <th>تحكم</th>
              </tr>
            </thead>
            <tbody>
              {students.map(student => (
                <tr key={student.id}>
                  <td>{student.name}</td>
                  <td>{student.email}</td>
                  <td>{student.attendance ?? 0}</td>
                  <td>{student.warnings ?? 0}</td>
                  <td>{student.completedLectures ?? 0}</td>
                  <td>{student.notes || 'لا يوجد'}</td>
                  <td>
                    <button className="btn btn-sm btn-primary me-2" onClick={() => { setSelectedStudent(student); setEditData(student); }}>تعديل</button>
                    {/* <button className="btn btn-sm btn-danger" onClick={() => handleReset(student.id)}>Reset</button> */}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedStudent && (
        <div className="mt-4 p-4 border rounded bg-light">
          <h5>تعديل بيانات: {selectedStudent.name}</h5>
          <div className="row">
            <div className="col-md-3">
              <label>الحضور:</label>
              <input type="number" className="form-control" name="attendance" value={editData.attendance} onChange={handleEditChange} />
            </div>
            <div className="col-md-3">
              <label>الإنذارات:</label>
              <input type="number" className="form-control" name="warnings" value={editData.warnings} onChange={handleEditChange} />
            </div>
            <div className="col-md-3">
              <label>المكتملة:</label>
              <input type="number" className="form-control" name="completedLectures" value={editData.completedLectures} onChange={handleEditChange} />
            </div>
            <div className="col-md-3">
              <label>ملاحظات:</label>
              <input type="text" className="form-control" name="notes" value={editData.notes} onChange={handleEditChange} />
            </div>
          </div>
          <button className="btn btn-success mt-3" onClick={handleEditSubmit}>حفظ التعديلات</button>
        </div>
      )}

      <h4 className="mt-5">الكورسات الخاصة بي</h4>
      {myCourses.length === 0 ? (
        <p>لم تقم بإنشاء أي كورس بعد.</p>
      ) : (
        <div className="row">
          {myCourses.map(course => (
            <div key={course.id} className="col-md-4 mb-3">
              <div className="card h-100 shadow-sm" style={{ flex: '1', textAlign: 'center', borderRadius: '10px' }}>
                {course.imageUrl && (
                  <img
                    src={course.imageUrl}
                    className="card-img-top"
                    alt={course.title}
                    style={{ objectFit: 'cover', height: '200px', width: '90%', margin: '0 auto', borderRadius: '10px', marginTop: '20px', marginBottom: '20px' }}
                  />
                )}
                <div className="card-body d-flex flex-column">
                  <h5 className="card-title">{course.title}</h5>
                  <p className="card-text flex-grow-1">{course.description}</p>
                  <Link to={`/courses/${course.id}/edit`} className="btn btn-warning mt-auto">
                    تعديل الكورس
                  </Link>
                 
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
